import axios from '../../api';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { AnnualCampaignMap } from './types';

function normalizeSegment(segment: any) {
  const { attributeProduct, ...normalizedSegment } = segment;

  return {
    ...normalizedSegment,
    ...normalizedSegment,
    attribute: !!attributeProduct
      ? {
          id: attributeProduct?.id,
          name: attributeProduct?.segment,
        }
      : segment.attribute,
  };
}

function normalizeACMSegments(data: any) {
  return {
    ...data,
    segments: data.segments?.map(segment => normalizeSegment(segment)) ?? [],
  };
}

function parseAsACM(data: any): AnnualCampaignMap {
  // parse product.brandModels.brandModel.contentDrops.date as Date & return the result
  return {
    ...normalizeACMSegments(data),
    product: {
      ...data.product,
      brandModels: data.product.brandModels.map(bm => ({
        ...bm,
        brandModel: {
          ...bm.brandModel,
          contentDrops: bm.brandModel.contentDrops.map(cd => ({
            ...cd,
            date: new Date(cd.date),
          })),
        },
      })),
    },
  };
}

export const getAnnualCampaignMaps = createAsyncThunk(
  'getAnnualCampaignMaps',
  async (all: boolean) => {
    const response = await axios.get(`/annual-campaign-maps/list/${all ? 'all' : 'user'}`);
    return response.data.map(normalizeACMSegments);
  },
);

export const getAnnualCampaignMap = createAsyncThunk(
  'getAnnualCampaignMap',
  async (displayId: string) => {
    const response = await axios.get(`/annual-campaign-maps/${displayId}`);
    return parseAsACM(response.data);
  },
);

export const createNewAnnualCampaignMap = createAsyncThunk(
  'createNewAnnualCampaignMap',
  async (payload: {
    description: string;
    productId: number;
    campaignTypeId: number;
    year: number;
    countryId: number;
  }) => {
    const response = await axios.post('/annual-campaign-maps', payload);
    return response.data;
  },
);

export const updateAnnualCampaignMap = createAsyncThunk(
  'updateAnnualCampaignMap',
  async (payload: {
    description: string;
    displayId: string;
    productId: number;
    campaignTypeId: number;
    year: number;
    countryId: number;
    statusId: number;
  }) => {
    const { displayId, ...request } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}`, request);
    return parseAsACM(response.data);
  },
);

export const updateAnnualCampaignMapTeam = createAsyncThunk(
  'updateAnnualCampaignMapTeam',
  async (payload: {
    displayId: string;
    team: { id: number }[];
  }) => {
    const { displayId, team } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/team`, {
      team: (team || []).map(({ id }) => ({ id })),
    });
    return parseAsACM(response.data);
  },
);

export const updateAnnualCampaignMapOwner = createAsyncThunk(
  'updateAnnualCampaignMapOwner',
  async (payload: {
    displayId: string;
    owner: { id: number };
  }) => {
    const { displayId, owner } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/owner`, { owner });
    return parseAsACM(response.data);
  },
);

export const updateOverallBehaviour = createAsyncThunk(
  'updateOverallBehaviour',
  async (payload: { displayId: string; from: string; to: string }) => {
    const { displayId, from, to } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/payload`, {
      overallBehaviour: { from, to },
    });
    return parseAsACM(response.data);
  },
);

export const updateSegmentsOrder = createAsyncThunk(
  'updateSegmentsOrder',
  async (payload: { displayId: string; order: number[]; previousOrder?: number[] }) => {
    const { displayId, order } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/payload`, {
      segmentsOrder: order,
    });
    return parseAsACM(response.data);
  },
);

export const updateAcmLink = createAsyncThunk(
  'updateAcmLink',
  async (payload: {
    displayId: string;
    field: 'editorialMasterplan' | 'brandExecutionGuide';
    value: string;
  }) => {
    const { displayId, field, value } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/payload`, {
      [field]: value,
    });
    return parseAsACM(response.data);
  },
);

export const cloneAnnualCampaignMap = createAsyncThunk(
  'cloneAnnualCampaignMap',
  async (payload: {
    productId?: number;
    campaignTypeId?: number;
    year?: number;
    countryId?: number;
    description: string;
    statusId?: number;
    segments?: any[];
    campaigns?: any[];
    keyDates?: any[];
  }) => {
    const response = await axios.post(`/annual-campaign-maps/1/clone`, payload);

    console.log('CLONE RESPONSE', response);
    return response.data;
  },
);

export const createNewSegment = createAsyncThunk(
  'createNewSegment',
  async (payload: { displayId: string; attributeId: number; from: string; to: string }) => {
    const { displayId, ...request } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/segments`, request);
    return normalizeSegment(response.data);
  },
);

export const editSegment = createAsyncThunk(
  'editSegment',
  async (payload: {
    displayId: string;
    segmentId: number;
    attributeId: number;
    from: string;
    to: string;
  }) => {
    const { displayId, segmentId, ...request } = payload;
    const response = await axios.post(
      `/annual-campaign-maps/${displayId}/segments/${segmentId}`,
      request,
    );
    return normalizeSegment(response.data);
  },
);

export const deleteSegment = createAsyncThunk(
  'deleteSegment',
  async (payload: { displayId: string; segmentId: number }) => {
    await axios.delete(`/annual-campaign-maps/${payload.displayId}/segments/${payload.segmentId}`);
    return {
      id: payload.segmentId,
    };
  },
);

export const createNewKeyDate = createAsyncThunk(
  'createNewKeyDate',
  async (payload: { displayId: string; name: string; from: Date; to?: Date }) => {
    const { displayId, ...request } = payload;
    const response = await axios.post(`/annual-campaign-maps/${displayId}/key-dates`, request);
    return response.data;
  },
);

export const editKeyDate = createAsyncThunk(
  'editKeyDate',
  async (payload: {
    displayId: string;
    keyDateId: number;
    name: string;
    from: Date;
    to?: Date;
  }) => {
    const { displayId, keyDateId, ...request } = payload;
    const response = await axios.post(
      `/annual-campaign-maps/${displayId}/key-dates/${keyDateId}`,
      request,
    );
    return response.data;
  },
);

export const deleteKeyDate = createAsyncThunk(
  'deleteKeyDate',
  async (payload: { displayId: string; keyDateId: number }) => {
    await axios.delete(`/annual-campaign-maps/${payload.displayId}/key-dates/${payload.keyDateId}`);
    return {
      id: payload.keyDateId,
    };
  },
);

export const addNewCampaign = createAsyncThunk(
  'addNewCampaign',
  async (
    payload: {
      displayId: string;
      name: string;
      startDate: Date;
      endDate: Date;
      contentDropId?: number;
    },
    { rejectWithValue },
  ) => {
    const body = {
      name: payload.name,
      startDate: payload.startDate.toISOString(),
      endDate: payload.endDate.toISOString(),
      contentDropId: payload.contentDropId,
    };
    try {
      const { data: campaigns } = await axios.post(
        `/annual-campaign-maps/${payload.displayId}/campaigns/new`,
        body,
      );
      return {
        campaigns,
      };
    } catch (error: any) {
      return rejectWithValue(error?.response?.status === 409 ? 'conflict' : undefined);
    }
  },
);

export const addExistingCampaign = createAsyncThunk(
  'addExistingCampaign',
  async (payload: { displayId: string; campaignId: number; contentDropId?: number; }) => {
    const { data: campaigns } = await axios.post(
      `/annual-campaign-maps/${payload.displayId}/campaigns/existing`,
      { campaignId: payload.campaignId , contentDropId: payload.contentDropId },
    );
    return {
      campaigns,
    };
  },
);

export const deleteCampaignFromACM = createAsyncThunk(
  'deleteCampaignFromACM',
  async (payload: { acmDisplayId: string; campaignDisplayId: string }) => {
    await axios.delete(
      `/annual-campaign-maps/${payload.acmDisplayId}/campaigns/${payload.campaignDisplayId}`,
    );
    return { campaignDisplayId: payload.campaignDisplayId };
  },
);
